<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  connectLilithAssistantInbox,
  disconnectLilithAssistantInbox,
  getLilithAssistantInboxes,
} from 'dashboard/api/lilithTeam';

const props = defineProps({
  assistantId: {
    type: String,
    required: true,
  },
  canManage: {
    type: Boolean,
    default: false,
  },
});

const { accountId } = useAccount();
const { t } = useI18n();
const store = useStore();
const accountInboxes = useMapGetter('inboxes/getInboxes');

const boundRefs = ref([]);
const connections = ref([]);
let pollTimer;
let generation = 0;
const connectionState = inboxRef => {
  const item = connections.value.find(row => String(row.inbox_ref) === String(inboxRef));
  if (!item) return '';
  return item.error_code ? t('TEAM_LILITH.INBOXES.CONNECTION_RETRY') : t(`TEAM_LILITH.INBOXES.CONNECTION_${item.state}`);
};
const selectedInboxId = ref('');
const loading = ref(false);
const saving = ref(false);
const disconnectArmed = ref('');

const inboxByRef = computed(
  () =>
    new Map(
      (accountInboxes.value || []).map(inbox => [String(inbox.id), inbox])
    )
);

const boundInboxes = computed(() =>
  boundRefs.value.map(inboxRef => {
    const inbox = inboxByRef.value.get(String(inboxRef));
    return (
      inbox || {
        id: inboxRef,
        name: t('TEAM_LILITH.INBOXES.UNKNOWN_INBOX', { id: inboxRef }),
        channel_type: '',
      }
    );
  })
);

const availableInboxes = computed(() => {
  const bound = new Set(boundRefs.value.map(String));
  return (accountInboxes.value || []).filter(
    inbox => !bound.has(String(inbox.id))
  );
});

const fetchBindings = async () => {
  const requestedGeneration = generation;
  const response = await getLilithAssistantInboxes(
    accountId.value,
    props.assistantId
  );
  if (requestedGeneration !== generation) return;
  connections.value = response.data?.connections || [];
  boundRefs.value = connections.value.filter(item => item.desired_active || item.state === 'PENDING').map(item => String(item.inbox_ref));
  clearTimeout(pollTimer);
  if (connections.value.some(item => item.state === 'PENDING')) {
    const current = generation;
    pollTimer = setTimeout(async () => {
      if (current !== generation) return;
      try { await fetchBindings(); } catch (error) {
        if (current !== generation) return;
        pollTimer = setTimeout(() => { if (current === generation) load(); }, 5000);
      }
    }, 2000);
  }
};

const load = async () => {
  selectedInboxId.value = '';
  disconnectArmed.value = '';
  loading.value = true;
  try {
    if (!(accountInboxes.value || []).length) {
      await store.dispatch('inboxes/get');
    }
    await fetchBindings();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.INBOXES.ERROR')
    );
  } finally {
    loading.value = false;
  }
};

const connectInbox = async () => {
  if (!props.canManage || !selectedInboxId.value || saving.value) return;
  saving.value = true;
  try {
    await connectLilithAssistantInbox(
      accountId.value,
      props.assistantId,
      selectedInboxId.value
    );
    selectedInboxId.value = '';
    await fetchBindings();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.INBOXES.ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const disconnectInbox = async inboxRef => {
  const normalized = String(inboxRef);
  if (!props.canManage || saving.value) return;
  if (disconnectArmed.value !== normalized) {
    disconnectArmed.value = normalized;
    return;
  }

  saving.value = true;
  try {
    await disconnectLilithAssistantInbox(
      accountId.value,
      props.assistantId,
      normalized
    );
    disconnectArmed.value = '';
    await fetchBindings();
    if (!connections.value.some(item => String(item.inbox_ref) === normalized && item.state === 'PENDING')) useAlert(t('TEAM_LILITH.INBOXES.DISCONNECTED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('TEAM_LILITH.INBOXES.ERROR')
    );
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.assistantId,
  () => { generation += 1; clearTimeout(pollTimer); load(); },
  { immediate: true }
);
onBeforeUnmount(() => { generation += 1; clearTimeout(pollTimer); });
</script>

<template>
  <div class="flex flex-1 overflow-y-auto bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-8 lg:px-10"
    >
      <header>
        <div
          class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
        >
          {{ $t('TEAM_LILITH.INBOXES.EYEBROW') }}
        </div>
        <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
          {{ $t('TEAM_LILITH.INBOXES.TITLE') }}
        </h1>
        <p class="mt-2 max-w-3xl text-sm leading-6 text-n-slate-11">
          {{ $t('TEAM_LILITH.INBOXES.DESCRIPTION') }}
        </p>
      </header>

      <section
        v-if="canManage"
        class="rounded-xl border border-n-weak bg-n-surface-2 p-4"
      >
        <div class="flex flex-col gap-3 md:flex-row md:items-end">
          <label
            class="flex flex-1 flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('TEAM_LILITH.INBOXES.CONNECT_LABEL') }}
            <select
              v-model="selectedInboxId"
              class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-sm text-n-slate-12"
              :disabled="loading || saving || !availableInboxes.length"
            >
              <option value="">
                {{ $t('TEAM_LILITH.INBOXES.CONNECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in availableInboxes"
                :key="inbox.id"
                :value="String(inbox.id)"
              >
                {{ inbox.name }}
                <span class="ml-2 text-xs font-normal">{{ connectionState(inbox.id) }}</span>
              </option>
            </select>
          </label>
          <Button
            :label="$t('TEAM_LILITH.INBOXES.CONNECT')"
            icon="i-lucide-plus"
            :disabled="loading || saving || !selectedInboxId"
            :is-loading="saving"
            @click="connectInbox"
          />
        </div>
        <p
          v-if="!loading && !availableInboxes.length"
          class="mt-2 text-xs text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.INBOXES.NO_AVAILABLE') }}
        </p>
      </section>

      <section class="flex flex-col gap-3">
        <div
          v-if="loading"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-6 text-sm text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.INBOXES.LOADING') }}
        </div>
        <div
          v-else-if="!boundInboxes.length"
          class="rounded-xl border border-n-weak bg-n-surface-2 p-6 text-sm text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.INBOXES.EMPTY') }}
        </div>
        <template v-else>
          <article
            v-for="inbox in boundInboxes"
            :key="String(inbox.id)"
            class="flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-surface-2 px-4 py-4"
          >
            <div class="min-w-0">
              <div class="truncate text-sm font-semibold text-n-slate-12">
                {{ inbox.name }}
                <span class="ml-2 text-xs font-normal">{{ connectionState(inbox.id) }}</span>
              </div>
              <div class="mt-1 text-xs text-n-slate-10">
                {{
                  inbox.channel_type ||
                  $t('TEAM_LILITH.INBOXES.INBOX_REF', { id: inbox.id })
                }}
              </div>
            </div>
            <Button
              v-if="canManage"
              :label="
                disconnectArmed === String(inbox.id)
                  ? $t('TEAM_LILITH.INBOXES.CONFIRM_DISCONNECT')
                  : $t('TEAM_LILITH.INBOXES.DISCONNECT')
              "
              size="sm"
              :variant="
                disconnectArmed === String(inbox.id) ? 'solid' : 'ghost'
              "
              color="ruby"
              :disabled="saving"
              @click="disconnectInbox(inbox.id)"
            />
          </article>
        </template>
      </section>
    </div>
  </div>
</template>
